import React, { useState, useEffect, useRef } from 'react'
import { Paper, Typography, CircularProgress, makeStyles, withStyles, Button } from '@material-ui/core'
import { Stepper, Step, StepLabel, StepConnector } from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import FolderOpenIcon from '@material-ui/icons/FolderOpen'
import ArchiveIcon from '@material-ui/icons/Archive'
import EmailIcon from '@material-ui/icons/Email'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import GetAppIcon from '@material-ui/icons/GetApp'
import AircraftIcon from '@material-ui/icons/FlightTakeoff'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import clsx from 'clsx'
import { globalGetService } from '../../utils/globalApiServices'
import { checkApiStatus } from '../../utils'

const STEPS = [
    { label: 'Initializing', desc: 'Setting up your download request', Icon: AutorenewIcon },
    { label: 'Building Structure', desc: 'Organizing files and folders', Icon: FolderOpenIcon },
    { label: 'Creating ZIP', desc: 'Compressing files into an archive', Icon: ArchiveIcon },
    { label: 'Link Sent', desc: 'Download link delivered to your email', Icon: EmailIcon },
]

const STEP_PERCENT = [10, 35, 65, 100]

// ── Custom stepper connector ─────────────────────────────────────────────────
const ColorlibConnector = withStyles({
    alternativeLabel: { top: 22 },
    active: { '& $line': { background: 'linear-gradient(90deg,#1565c0 0%,#42a5f5 100%)' } },
    completed: { '& $line': { background: 'linear-gradient(90deg,#1565c0 0%,#42a5f5 100%)' } },
    line: { height: 3, border: 0, backgroundColor: '#dde3ed', borderRadius: 2 },
})(StepConnector)

// ── Custom step icon ─────────────────────────────────────────────────────────
const useStepIconStyles = makeStyles({
    root: {
        backgroundColor: '#dde3ed',
        color: '#fff',
        width: 46, height: 46,
        display: 'flex', borderRadius: '50%',
        justifyContent: 'center', alignItems: 'center',
        transition: 'all 0.4s ease',
    },
    active: { background: 'linear-gradient(135deg,#1565c0 0%,#42a5f5 100%)', boxShadow: '0 0 0 6px rgba(33,150,243,0.18)' },
    completed: { background: 'linear-gradient(135deg,#1565c0 0%,#42a5f5 100%)' },
    failed: { background: 'linear-gradient(135deg,#c62828 0%,#ef5350 100%)' },
    spin: { animation: '$spin 1.2s linear infinite' },
    '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
})

function ColorlibStepIcon({ active, completed, icon, failed }) {
    const classes = useStepIconStyles()
    const stepIndex = icon - 1
    const { Icon } = STEPS[stepIndex] || {}
    const isActive = active && !failed
    const isFailed = active && failed

    return (
        <div className={clsx(classes.root, {
            [classes.active]: isActive,
            [classes.completed]: completed,
            [classes.failed]: isFailed,
        })}>
            {completed ? (
                <CheckCircleIcon style={{ fontSize: 22 }} />
            ) : isFailed ? (
                <ErrorIcon style={{ fontSize: 22 }} />
            ) : Icon ? (
                <Icon style={{ fontSize: 22 }} className={clsx({ [classes.spin]: isActive && stepIndex === 0 })} />
            ) : (
                <RadioButtonUncheckedIcon style={{ fontSize: 22 }} />
            )}
        </div>
    )
}

// ── Page styles ──────────────────────────────────────────────────────────────
const useStyles = makeStyles({
    page: { minHeight: '85vh', backgroundColor: '#f0f4f8', padding: '55px 0px' },
    logoRow: {
        position: 'relative',
        left: '44%',
        marginBottom: 24,
        width: '0px'
    },
    card: {
        margin: '40px',
        maxWidth: 800, margin: '0 auto',
        padding: '36px 32px 28px', borderRadius: 16,
        boxShadow: '0 4px 32px rgba(30,60,120,0.10)',
    },

    // ── Asset meta strip ──
    metaStrip: {
        display: 'flex', gap: 12, flexWrap: 'wrap',
        marginBottom: 32,
    },
    metaChip: {
        display: 'flex', alignItems: 'center', gap: 6,
        backgroundColor: '#eef3fb', borderRadius: 8,
        padding: '6px 14px',
        margin:'0 auto'
    },
    metaLabel: { fontSize: 11, color: '#6b7c93', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
    metaValue: { fontSize: 14, color: '#1a2a4a', fontWeight: 600 },

    // ── Progress ring ──
    centerBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 32 },
    progressRing: {
        position: 'relative', width: 110, height: 110, marginBottom: 20,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    progressFill: { position: 'absolute', transition: 'all 0.8s ease' },
    progressIcon: { position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    progressPercent: { fontSize: 18, fontWeight: 700, color: '#1565c0', lineHeight: 1 },
    statusTitle: { fontWeight: 700, fontSize: 22, color: '#1a2a4a', marginBottom: 6, textAlign: 'center' },
    statusSubtitle: { fontSize: 14, color: '#6b7c93', textAlign: 'center', maxWidth: 440 },
    pulseRing: { animation: '$pulse 2s ease-in-out infinite' },
    '@keyframes pulse': {
        '0%': { boxShadow: '0 0 0 0 rgba(33,150,243,0.4)' },
        '70%': { boxShadow: '0 0 0 16px rgba(33,150,243,0)' },
        '100%': { boxShadow: '0 0 0 0 rgba(33,150,243,0)' },
    },

    // ── Stepper ──
    stepper: { backgroundColor: 'transparent', padding: '8px 0' },

    // ── Status description bar ──
    statusBar: {
        marginTop: 24, padding: '14px 20px', borderRadius: 10,
        display: 'flex', alignItems: 'center', gap: 10,
    },
    statusBarDefault: { backgroundColor: '#eaf2ff', borderLeft: '4px solid #1565c0' },
    statusBarError: { backgroundColor: '#fff0f0', borderLeft: '4px solid #c62828' },
    statusBarSuccess: { backgroundColor: '#eafaf1', borderLeft: '4px solid #1b5e20' },
    statusBarText: { fontSize: 14, fontWeight: 500 },
    textDefault: { color: '#1a3a6a' },
    textError: { color: '#8b0000' },
    textSuccess: { color: '#1b5e20' },

    // ── Download CTA ──
    downloadSection: {
        marginTop: 24, padding: '20px 24px',
        borderRadius: 12, backgroundColor: '#f0faf4',
        border: '1px solid #c8e6c9',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
    },
    downloadTextBlock: { flex: 1 },
    downloadTitle: { fontSize: 15, fontWeight: 700, color: '#1b5e20', marginBottom: 2 },
    downloadSubtext: { fontSize: 13, color: '#388e3c' },
    downloadBtn: {
        background: 'linear-gradient(135deg,#1b5e20 0%,#43a047 100%)',
        color: '#fff', fontWeight: 700, padding: '10px 28px',
        borderRadius: 8, textTransform: 'none', fontSize: 14,
        '&:hover': { background: 'linear-gradient(135deg,#145214 0%,#2e7d32 100%)' },
    },
})

export default function DownloadStatus({ match, history }) {
    const classes = useStyles()
    const [activeStep, setActiveStep] = useState(0)
    const [failed, setFailed] = useState(false)
    const [folderName, setFolderName] = useState('')
    const [downloadUrl, setDownloadUrl] = useState('')
    const intervalRef = useRef(null)
    const downloadBtnRef = useRef(null)

    const uuid = match?.params?.uuid

    const fetchStatus = () => {
        globalGetService(`records/download-zip-status/${uuid}/`)
            .then(response => {
                    const { stage, folder_name, folder_path } = response.data.data
                    const cleanedFolderName = folder_name?.replace(/, +$/, '')
                    if (folder_name) setFolderName(cleanedFolderName)
                    if (folder_path) setDownloadUrl(folder_path)
                    if (stage === 4) {
                        setFailed(true)
                        setActiveStep(3)
                        clearInterval(intervalRef.current)
                    } else {
                        setActiveStep(stage)
                        if (stage === 3) {
                            if(!window.location.href.includes('download=true')){
                            downloadBtnRef.current?.click()
                            }
                            setTimeout(()=>{history.replace({ search: '?download=true' })},500)
                            clearInterval(intervalRef.current)
                        }
                    }
                })
            }
            
    useEffect(() => {
        if (!uuid) return
        fetchStatus()
        intervalRef.current = setInterval(fetchStatus, 30000)
        return () => clearInterval(intervalRef.current)
    }, [uuid])  

    const isDone = activeStep === 3 && !failed
    const currentStep = STEPS[activeStep]
    return (
        <section className={classes.page}>
            <Paper className={classes.card} elevation={0}>

                {/* ── Logo ── */}
                <div className={classes.logoRow}>
                    <img
                        width="62"
                        height='53'
                        src="https://sparta-aims.ams3.cdn.digitaloceanspaces.com/others/assets/img/sparta_logo.png"
                        alt="Sparta Logo"
                    />
                </div>

                {/* ── Asset / folder meta strip ── */}
                {(folderName) && (
                    <div className={classes.metaStrip}>
                        {folderName && (
                            <div className={classes.metaChip}>
                                <FolderOpenIcon style={{ fontSize: 16, color: '#1565c0' }} />
                                <div>
                                    <Typography className={classes.metaLabel}>Folder</Typography>
                                    <Typography className={classes.metaValue}>{folderName}</Typography>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ── Progress ring + title ── */}
                <div className={classes.centerBox}>

                    <Typography className={classes.statusTitle}>
                        {isDone ? 'Download Ready!' : failed ? 'Download Failed' : 'Preparing Your Download'}
                    </Typography>
                    <Typography className={classes.statusSubtitle}>
                        {isDone
                            ? 'A download link has been sent to your registered email address.'
                            : failed
                                ? 'Something went wrong while processing your download. Please try again.'
                                : "Keep this page open — we'll notify you by email when your download is ready."}
                    </Typography>
                </div>

                {/* ── Stepper ── */}
                <Stepper
                    activeStep={failed ? -1 : activeStep}
                    alternativeLabel
                    connector={<ColorlibConnector />}
                    className={classes.stepper}
                >
                    {STEPS.map((step, index) => (
                        <Step key={step.label}>
                            <StepLabel
                                StepIconComponent={(props) => (
                                    <ColorlibStepIcon {...props} failed={failed && index === activeStep} />
                                )}
                            >
                                <Typography style={{
                                    fontSize: 12,
                                    fontWeight: activeStep === index ? 700 : 400,
                                    color: activeStep === index ? '#1565c0' : '#6b7c93',
                                }}>
                                    {step.label}
                                </Typography>
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {/* ── Status description bar ── */}
                {currentStep && (
                    <div className={clsx(classes.statusBar, {
                        [classes.statusBarError]: failed,
                        [classes.statusBarSuccess]: isDone,
                        [classes.statusBarDefault]: !failed && !isDone,
                    })}>
                        {isDone ? (
                            <EmailIcon style={{ color: '#2e7d32', fontSize: 20, flexShrink: 0 }} />
                        ) : failed ? (
                            <ErrorIcon style={{ color: '#c62828', fontSize: 20, flexShrink: 0 }} />
                        ) : (
                            <AutorenewIcon style={{ color: '#1565c0', fontSize: 20, flexShrink: 0, animation: 'spin 1.5s linear infinite' }} />
                        )}
                        <Typography className={clsx(classes.statusBarText, {
                            [classes.textError]: failed,
                            [classes.textSuccess]: isDone,
                            [classes.textDefault]: !failed && !isDone,
                        })}>
                            {isDone
                                ? 'Your ZIP archive is ready. Check your inbox for the download link.'
                                : failed
                                    ? 'Download preparation failed. Please contact support or retry the download.'
                                    : `Currently: ${currentStep.desc}…`}
                        </Typography>
                    </div>
                )}

                {/* ── Download CTA (shown only when done) ── */}
                {isDone && (
                    <div className={classes.downloadSection}>
                        <div className={classes.downloadTextBlock}>
                            <Typography className={classes.downloadTitle}>Your ZIP archive is ready</Typography>
                            <Typography className={classes.downloadSubtext}>
                                Click the button to start downloading, or use the link sent to your email.
                            </Typography>
                        </div>
                        <Button
                            ref={downloadBtnRef}
                            variant="contained"
                            color='primary'
                            size='small'
                            startIcon={<GetAppIcon />}
                            href={downloadUrl || undefined}
                            disabled={!downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Download ZIP
                        </Button>
                    </div>
                )}

            </Paper>
        </section>
    )
}
